import get from 'lodash/get';
import axiosConfig from './axiosConfig';

const DOMAIN = 'spacer_log';

const unwrap = (res) => {
  const top = get(res, 'data', null);
  const nested = get(res, ['data', 'data'], null);
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested;
  if (top && typeof top === 'object' && !Array.isArray(top)) return top;
  return null;
};

const coerceUsageObject = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
};

const estimateCostUsd = (inputTokens, outputTokens) => {
  const inTok = Number.isFinite(inputTokens) ? inputTokens : 0;
  const outTok = Number.isFinite(outputTokens) ? outputTokens : 0;
  // Match pih-server estimateInsightCost defaults (Sonnet-ish list prices).
  const usd = (inTok / 1_000_000) * 3 + (outTok / 1_000_000) * 15;
  return Math.round(usd * 100) / 100;
};

const extractUsageFields = (src) => {
  if (!src || typeof src !== 'object') return null;
  const inputTokens = Number(
    src.inputTokens
      ?? src.input_tokens
      ?? src.promptTokens
      ?? src.prompt_tokens
      ?? src.input
      ?? NaN
  );
  const outputTokens = Number(
    src.outputTokens
      ?? src.output_tokens
      ?? src.completionTokens
      ?? src.completion_tokens
      ?? src.output
      ?? NaN
  );
  let estimatedCostUsd = Number(
    src.estimatedCostUsd
      ?? src.estimated_cost_usd
      ?? src.estCostUsd
      ?? src.costUsd
      ?? src.cost_usd
      ?? src.totalCost
      ?? src.total_cost
      ?? src.cost
      ?? NaN
  );
  const hasTokens = Number.isFinite(inputTokens) || Number.isFinite(outputTokens);
  if (!Number.isFinite(estimatedCostUsd) && hasTokens) {
    estimatedCostUsd = estimateCostUsd(inputTokens, outputTokens);
  }
  const hasAny =
    Number.isFinite(inputTokens) || Number.isFinite(outputTokens) || Number.isFinite(estimatedCostUsd);
  if (!hasAny) return null;
  return {
    inputTokens: Number.isFinite(inputTokens) ? inputTokens : null,
    outputTokens: Number.isFinite(outputTokens) ? outputTokens : null,
    estimatedCostUsd: Number.isFinite(estimatedCostUsd) ? estimatedCostUsd : null,
  };
};

/**
 * Normalize usage from generate or chat-history assistant payloads.
 * Accepts nested usage / snake_case / stringified JSON / flat message fields.
 */
export const normalizeUsage = (raw) => {
  const parsed = coerceUsageObject(raw);
  if (!parsed) return null;

  const nestedKeys = ['usage', 'tokenUsage', 'token_usage', 'tokens', 'cost'];
  for (let i = 0; i < nestedKeys.length; i += 1) {
    const nested = coerceUsageObject(parsed[nestedKeys[i]]);
    const hit = extractUsageFields(nested);
    if (hit) return hit;
  }

  const fromMeta = extractUsageFields(coerceUsageObject(parsed.meta?.usage || parsed.metadata?.usage));
  if (fromMeta) return fromMeta;

  return extractUsageFields(parsed);
};

/** Pull usage from a chat-history assistant message (any known shape). */
export const pickUsageFromMessage = (m) => {
  if (!m || typeof m !== 'object') return null;
  return (
    normalizeUsage(m.usage)
    || normalizeUsage(m.tokenUsage)
    || normalizeUsage(m.token_usage)
    || normalizeUsage(m.meta)
    || normalizeUsage(m.metadata)
    || normalizeUsage(m.stats)
    || normalizeUsage({
      inputTokens: m.inputTokens ?? m.input_tokens,
      outputTokens: m.outputTokens ?? m.output_tokens,
      estimatedCostUsd: m.estimatedCostUsd ?? m.estimated_cost_usd ?? m.costUsd,
    })
    || null
  );
};

const USAGE_CACHE_KEY = 'askEvansUsageByFeedbackId';

/** Remember per-answer usage so history reopen still shows it in this browser session. */
export const cacheUsageForFeedbackTarget = (feedbackTargetId, usage) => {
  const u = normalizeUsage(usage);
  if (feedbackTargetId == null || feedbackTargetId === '' || !u) return;
  try {
    const map = JSON.parse(sessionStorage.getItem(USAGE_CACHE_KEY) || '{}');
    map[String(feedbackTargetId)] = u;
    sessionStorage.setItem(USAGE_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
};

export const readCachedUsage = (feedbackTargetId) => {
  if (feedbackTargetId == null || feedbackTargetId === '') return null;
  try {
    const map = JSON.parse(sessionStorage.getItem(USAGE_CACHE_KEY) || '{}');
    return normalizeUsage(map[String(feedbackTargetId)]);
  } catch {
    return null;
  }
};

export const normalizeQuota = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const used = Number(raw.used ?? raw.tokensUsed ?? raw.tokens_used ?? NaN);
  const limit = Number(raw.limit ?? raw.tokensLimit ?? raw.tokens_limit ?? NaN);
  if (!Number.isFinite(used) || !Number.isFinite(limit) || limit <= 0) return null;
  return { used, limit };
};

export const formatTokenCount = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('en-US');
};

export const formatUsd = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toFixed(2)}`;
};

/**
 * Discover AI insight features + availability flags.
 * GET /pih/ai/insights/features?domain=spacer_log
 */
export const fetchAiInsightFeatures = async ({ domain = DOMAIN } = {}) => {
  const res = await axiosConfig.get('ai/insights/features', {
    params: { domain },
  });
  const data = unwrap(res);
  if (!data || typeof data !== 'object') {
    throw new Error('AI insights features response was empty or invalid.');
  }
  return {
    enabled: data.enabled !== false,
    provider: data.provider || null,
    bedrockModelId: data.bedrockModelId || null,
    ragConfigured: Boolean(data.ragConfigured),
    features: Array.isArray(data.features) ? data.features : [],
    quota: normalizeQuota(data.quota),
  };
};

/**
 * Generate an insight (AI or template). Supports AbortSignal for cancel.
 * POST /pih/ai/insights
 */
export const generateAiInsight = async (body, { signal } = {}) => {
  const payload = {
    domain: DOMAIN,
    ...body,
  };
  const res = await axiosConfig.post('ai/insights', payload, { signal });
  const data = unwrap(res);
  if (!data || typeof data !== 'object') {
    throw new Error('AI insight response was empty or invalid.');
  }
  return {
    ...data,
    // POST /pih/ai/insights → data.usage { inputTokens, outputTokens, estimatedCostUsd }
    usage: normalizeUsage(data.usage) || pickUsageFromMessage(data),
    // rag_qa may also return data.quota { used, limit }
    quota: normalizeQuota(data.quota),
    feedbackTargetId:
      data.feedbackTargetId
      || data.feedbackId
      || data.queryLogId
      || data.id
      || null,
    chatSessionId: data.chatSessionId || data.chat_session_id || null,
    ragSessionId: data.ragSessionId || data.rag_session_id || data.sessionId || null,
  };
};

const unwrapSessions = (data) => {
  if (!data) return [];
  if (Array.isArray(data.sessions)) return data.sessions;
  if (Array.isArray(data)) return data;
  return [];
};

/** Relative label for chat list timestamps */
export const formatChatRelativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return 'Just now';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(t).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Map persisted thread messages into Ask Evans UI message shape.
 * Assistant `feedback.rating` restores thumbs without a separate GET.
 */
export const normalizeFeedbackRating = (feedback) => {
  if (feedback == null) return null;
  if (typeof feedback === 'string' || typeof feedback === 'number') {
    if (feedback === 'good' || feedback === 1) return 'good';
    if (feedback === 'bad' || feedback === -1) return 'bad';
    return null;
  }
  const rating = feedback.rating ?? feedback.value ?? null;
  if (rating === 'good' || rating === 1) return 'good';
  if (rating === 'bad' || rating === -1) return 'bad';
  return null;
};

export const mapChatThreadToMessages = (messages = []) => {
  const out = [];
  let lastUserQuestion = '';
  (messages || []).forEach((m, index) => {
    const role = m.role === 'assistant' || m.role === 'bot' ? 'assistant' : 'user';
    const content = m.content || m.text || m.markdown || m.message || '';
    const msgId = m.id ?? m.messageId ?? m.feedbackTargetId ?? `${role}-${index}`;
    if (role === 'user') {
      lastUserQuestion = content;
      out.push({
        id: `u-${msgId}`,
        role: 'user',
        text: content,
        createdAt: m.createdAt,
      });
    } else {
      const feedbackTargetId =
        m.feedbackTargetId
        || m.feedback_target_id
        || m.feedbackId
        || m.queryLogId
        || m.id
        || null;
      // GET …/chats/:id → assistant.usage (or flat / meta / session cache)
      const usage =
        pickUsageFromMessage(m)
        || readCachedUsage(feedbackTargetId);
      out.push({
        id: `a-${msgId}`,
        role: 'assistant',
        markdown: content,
        citations: m.citations,
        feature: m.feature,
        question: lastUserQuestion,
        usage,
        feedbackTargetId,
        feedbackRating: normalizeFeedbackRating(m.feedback || m.feedbackRating || m.rating),
        ragSessionId: m.ragSessionId || m.rag_session_id || null,
        createdAt: m.createdAt,
      });
    }
  });
  return out;
};

/** GET /pih/ai/insights/chats */
export const listAiInsightChats = async ({
  domain = DOMAIN,
  projectId,
  limit = 30,
} = {}) => {
  const params = { domain, limit };
  if (projectId != null && projectId !== '' && projectId !== 'all') {
    params.projectId = projectId;
  }
  const res = await axiosConfig.get('ai/insights/chats', { params });
  const data = unwrap(res) || res?.data || {};
  return {
    sessions: unwrapSessions(data).map((s) => ({
      id: s.id,
      title: s.title || 'Untitled chat',
      feature: s.feature || null,
      projectId: s.projectId ?? null,
      sowId: s.sowId ?? null,
      ragSessionId: s.ragSessionId || null,
      createdAt: s.createdAt || null,
      updatedAt: s.updatedAt || s.createdAt || null,
    })),
  };
};

/** GET /pih/ai/insights/chats/:id */
export const getAiInsightChat = async (id, { domain = DOMAIN } = {}) => {
  const res = await axiosConfig.get(`ai/insights/chats/${encodeURIComponent(id)}`, {
    params: { domain },
  });
  const data = unwrap(res);
  if (!data || typeof data !== 'object') {
    throw new Error('Chat thread response was empty or invalid.');
  }
  const session = data.session || data.thread || data;
  const messages =
    (Array.isArray(data.messages) && data.messages)
    || (Array.isArray(session.messages) && session.messages)
    || (Array.isArray(data.thread?.messages) && data.thread.messages)
    || (Array.isArray(data.items) && data.items)
    || [];
  return {
    session: {
      id: session.id || data.id || id,
      title: session.title || data.title || 'Untitled chat',
      feature: session.feature || data.feature || null,
      projectId: session.projectId ?? data.projectId ?? null,
      sowId: session.sowId ?? data.sowId ?? null,
      ragSessionId: session.ragSessionId || data.ragSessionId || null,
      createdAt: session.createdAt || data.createdAt || null,
      updatedAt: session.updatedAt || data.updatedAt || null,
    },
    messages,
  };
};

/** DELETE /pih/ai/insights/chats/:id */
export const deleteAiInsightChat = async (id) => {
  const res = await axiosConfig.delete(`ai/insights/chats/${encodeURIComponent(id)}`);
  return unwrap(res) || res?.data || { ok: true };
};

/** POST /pih/ai/insights/feedback — thumbs good/bad */
export const submitAiInsightFeedback = async ({
  feedbackTargetId,
  rating,
  domain = DOMAIN,
  feature,
  question,
  comment,
  answerPreview,
  markdown,
  projectId,
  ragSessionId,
} = {}) => {
  const payload = {
    domain,
    feedbackTargetId,
    rating: rating === 1 || rating === 'good' ? 'good' : rating === -1 || rating === 'bad' ? 'bad' : rating,
  };
  if (feature) payload.feature = feature;
  if (question) payload.question = question;
  if (comment) payload.comment = comment;
  const preview = answerPreview || markdown;
  if (preview) {
    payload.answerPreview = String(preview).slice(0, 2000);
  }
  if (projectId != null && projectId !== '') payload.projectId = projectId;
  if (ragSessionId) payload.ragSessionId = ragSessionId;
  const res = await axiosConfig.post('ai/insights/feedback', payload);
  return unwrap(res) || res?.data || { ok: true };
};

/** POST /pih/ai/insights/pdf — markdown → PDF blob */
export const downloadAiInsightPdf = async ({
  markdown,
  title = 'Ask Evans Report',
  domain = DOMAIN,
  feature,
  question,
} = {}) => {
  const payload = { domain, markdown, title };
  if (feature) payload.feature = feature;
  if (question) payload.question = question;
  const res = await axiosConfig.post('ai/insights/pdf', payload, {
    responseType: 'blob',
  });
  const blob = res?.data instanceof Blob
    ? res.data
    : new Blob([res?.data], { type: 'application/pdf' });
  if (!blob || blob.size === 0) {
    throw new Error('PDF response was empty.');
  }
  if (blob.type && blob.type.includes('json')) {
    const text = await blob.text();
    let msg = 'Failed to download PDF.';
    try {
      const parsed = JSON.parse(text);
      msg = parsed.message || parsed.error || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return blob;
};

export const saveBlobAsFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const AI_INSIGHTS_DOMAIN = DOMAIN;

export const SPACER_LOG_AI_FEATURES = {
  PERIOD_BRIEF: 'period_brief',
  SHIFT_BRIEF: 'shift_brief',
  BOTTLENECK_COACH: 'bottleneck_coach',
  COMMENT_THEMES: 'comment_themes',
  COMPLIANCE_ASSISTANT: 'compliance_assistant',
  ANOMALY_EXPLAIN: 'anomaly_explain',
  NL_QA: 'nl_qa',
  RAG_QA: 'rag_qa',
};

export const DAILY_AI_FEATURE_IDS = [
  SPACER_LOG_AI_FEATURES.SHIFT_BRIEF,
  SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH,
  SPACER_LOG_AI_FEATURES.COMMENT_THEMES,
  SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT,
  SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN,
];

export const routeAskQuestion = (question, ragConfigured) => {
  const q = String(question || '').toLowerCase();
  const looksLikeDocs =
    /sop|procedure|manual|policy|report wording|close-?out checklist|golfinho|document|pdf/i.test(
      q
    );
  if (looksLikeDocs && ragConfigured) return SPACER_LOG_AI_FEATURES.RAG_QA;
  return SPACER_LOG_AI_FEATURES.NL_QA;
};

export const isAiInsightsUiEnabled = () => {
  const flag = process.env.REACT_APP_AI_INSIGHTS;
  if (flag == null || flag === '') return true;
  return flag === 'true' || flag === '1';
};
